import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ══════════════════════════════════════════════════════
//  UNIFIED SHIPPING LOGIC
//  • AP & Telangana → base ₹80, others → base ₹100
//  • Blouses: base covers first 5, +₹15 per extra blouse
//  • Sarees:  base covers 1st, +half-base per extra saree
//  • Mixed cart: highest category charge wins
// ══════════════════════════════════════════════════════
const AP_TS_STATES = ["Andhra Pradesh", "Telangana"];
const getStateShippingRate = (stateName) => {
  if (!stateName) return 100;
  const normalized = stateName.trim().toLowerCase();
  return AP_TS_STATES.some(s => s.toLowerCase() === normalized) ? 80 : 100;
};

const isBlouse = (item) =>
  /blouse|sleeve|neck|dtf print/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);
const isSaree = (item) =>
  /saree|sari/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);

const BLOUSE_FREE_QTY   = 5;
const BLOUSE_EXTRA_RATE = 10;  // ✅ was 15
const BLOUSE_BASE_RATE  = 80;  // ✅ NEW — flat pan-India rate for
                                // blouses only; no longer varies by
                                // state (was using the AP/Telangana
                                // vs other-states BASE like every
                                // other category)
const SAREE_FREE_QTY    = 1;

// eslint-disable-next-line no-unused-vars
const calculateShipping = (items, stateName) => {
  if (!items || items.length === 0) return 0;
  const BASE = getStateShippingRate(stateName);
  const SAREE_EXTRA_RATE = BASE / 2;

  let blouseQty = 0, sareeQty = 0, otherQty = 0;
  items.forEach(item => {
    const qty = item.quantity || 1;
    if      (isBlouse(item)) blouseQty += qty;
    else if (isSaree(item))  sareeQty  += qty;
    else                     otherQty  += qty;
  });

  // ✅ Blouses now use the flat BLOUSE_BASE_RATE (₹80, pan-India)
  // instead of the state-dependent BASE — sarees and "other" below
  // are completely untouched, still state-based as before.
  const blouseCharge = blouseQty > 0
    ? BLOUSE_BASE_RATE + Math.max(0, blouseQty - BLOUSE_FREE_QTY) * BLOUSE_EXTRA_RATE : 0;
  const sareeCharge = sareeQty > 0
    ? BASE + Math.max(0, sareeQty - SAREE_FREE_QTY) * SAREE_EXTRA_RATE : 0;
  const otherCharge = otherQty > 0 ? BASE : 0;

  const active = [blouseCharge, sareeCharge, otherCharge].filter(c => c > 0);
  return active.length > 0 ? Math.max(...active) : 0;
};

// ── Login Modal ───────────────────────────────────────
function LoginModal({ onClose, onSuccess }) {
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [logoBroken, setLogoBroken] = useState(false);

  const handleGoogle = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        const res  = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const user = await res.json();
        await fetch(`${API}/api/auth/login`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ email: user.email, name: user.name, photo: user.picture }),
        });
        localStorage.setItem("userEmail", user.email);
        localStorage.setItem("userName",  user.name);
        localStorage.setItem("userPhoto", user.picture);
        localStorage.setItem("userRole",  "user");
        localStorage.setItem("authToken", tokenResponse.access_token);
        onSuccess();
      } catch { setError("Failed to sign in. Please try again."); }
      finally  { setLoading(false); }
    },
    onError: () => setError("Google login failed."),
  });

  const handleGuest = () => {
    localStorage.setItem("userRole", "guest");
    onSuccess();
  };

  return (
    <>
      <style>{`
        @keyframes overlayIn { from{opacity:0} to{opacity:1} }
        @keyframes modalIn   { from{opacity:0;transform:scale(0.95) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes spin      { to{transform:rotate(360deg)} }
        .ms { width:18px;height:18px;border-radius:50%;border:2px solid rgba(92,46,31,.2);
              border-top-color:#5c2e1f;animation:spin .7s linear infinite;
              display:inline-block;vertical-align:middle;margin-right:8px; }
        .login-modal-box {
          position:fixed;top:50%;left:50%;z-index:9999;
          transform:translate(-50%,-50%);
          width:min(92vw,860px); max-height:90vh; overflow-y:auto;
          background:#fff; border-radius:16px; overflow-x:hidden;
          display:flex; box-shadow:0 24px 80px rgba(0,0,0,0.35);
          animation:modalIn .25s ease;
        }
        .login-left {
          width:340px; flex-shrink:0;
          background:linear-gradient(160deg,#5c2e1f 0%,#5c2800 100%);
          display:flex; flex-direction:column; align-items:center;
          justify-content:center; padding:48px 36px;
        }
        .login-right {
          flex:1; padding:48px 40px; display:flex;
          flex-direction:column; justify-content:center;
          background:#fffaf4; position:relative; min-width:0;
        }
        @media (max-width:640px) {
          .login-modal-box { flex-direction:column; max-height:94vh; }
          .login-left  { width:100%; padding:32px 28px 24px; }
          .login-right { padding:28px 24px 32px; }
          .login-trust-row { margin-bottom:10px !important; }
        }
      `}</style>
      <div onClick={onClose} style={{ position:"fixed",inset:0,zIndex:9998,
        background:"rgba(0,0,0,0.55)",backdropFilter:"blur(3px)",
        animation:"overlayIn .2s ease" }}/>
      <div className="login-modal-box">
        <div className="login-left">
          <div style={{ width:88,height:88,borderRadius:"50%",
            border:"3px solid rgba(201,162,74,0.5)",margin:"0 auto 20px",
            overflow:"hidden",background:"rgba(255,255,255,0.12)",
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            {!logoBroken ? (
              <img src="/images/flogo.png" alt="Mana Vastralu"
                style={{ width:"100%",height:"100%",objectFit:"cover" }}
                onError={() => setLogoBroken(true)}/>
            ) : (
              <span style={{ fontSize:34 }} aria-hidden="true">🥻</span>
            )}
          </div>
          <h2 style={{ fontFamily:"Georgia,serif",fontSize:22,color:"#f5e6c8",margin:"0 0 6px",fontWeight:700 }}>
            Mana Vastralu
          </h2>
          <p style={{ color:"#c9a24a",fontSize:10,letterSpacing:"2.5px",textTransform:"uppercase",margin:"0 0 28px" }}>
            Mana Gurthimpu
          </p>
          {[["🛍️","2000+ Designs","Handpicked sarees"],
            ["🚚","Pan-India Delivery","Fast & trackable"],
            // ["✅","Easy Returns","30-day policy"],
          ].map(([icon,title,sub]) => (
            <div key={title} className="login-trust-row" style={{ display:"flex",gap:12,marginBottom:14,alignItems:"center",textAlign:"left" }}>
              <span style={{ fontSize:18,flexShrink:0 }}>{icon}</span>
              <div>
                <div style={{ color:"#f5e6c8",fontSize:13,fontWeight:600 }}>{title}</div>
                <div style={{ color:"#8a4a30",fontSize:11 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="login-right">
          <button onClick={onClose} style={{ position:"absolute",top:18,right:18,
            width:32,height:32,borderRadius:"50%",border:"1.5px solid #ecd9bd",
            background:"#fff",cursor:"pointer",fontSize:16,color:"#9a6040",
            display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
          <h2 style={{ fontFamily:"Georgia,serif",fontSize:24,color:"#3b1000",margin:"0 0 8px",fontWeight:600 }}>
            Sign in to continue
          </h2>
          <p style={{ color:"#9a6040",fontSize:14,margin:"0 0 28px",lineHeight:1.6 }}>
            Sign in to track orders and access exclusive member offers.
          </p>
          {error && (
            <div style={{ background:"#fff0f0",border:"1px solid #fca5a5",color:"#b91c1c",
              borderRadius:10,padding:"10px 14px",fontSize:13,marginBottom:20 }}>
              ⚠️ {error}
            </div>
          )}
          <button onClick={() => handleGoogle()} disabled={loading}
            style={{ width:"100%",padding:"14px 20px",background:"#fff",
              border:"1.5px solid #ddc89a",borderRadius:12,
              cursor:loading?"not-allowed":"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",gap:12,
              fontSize:15,fontWeight:600,color:"#3b1000",
              boxShadow:"0 2px 10px rgba(92,46,31,0.08)",
              transition:"all .2s",marginBottom:14,opacity:loading?0.7:1 }}>
            {loading ? <span className="ms"/> : (
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {loading ? "Signing in…" : "Continue with Google"}
          </button>
          <div style={{ display:"flex",alignItems:"center",gap:12,margin:"4px 0 14px" }}>
            <div style={{ flex:1,height:1,background:"#ecd9bd" }}/>
            <span style={{ fontSize:12,color:"#b08060" }}>or</span>
            <div style={{ flex:1,height:1,background:"#ecd9bd" }}/>
          </div>
          <button onClick={handleGuest}
            style={{ width:"100%",padding:"14px",
              background:"linear-gradient(135deg,#5c2e1f,#c0622a)",
              border:"none",borderRadius:12,cursor:"pointer",
              fontSize:14,fontWeight:600,color:"#fff",letterSpacing:"0.8px",
              textTransform:"uppercase",boxShadow:"0 4px 16px rgba(92,46,31,0.28)" }}>
            Continue as Guest
          </button>
          <div style={{ display:"flex",gap:0,marginTop:28,paddingTop:20,borderTop:"1px solid #f2ddc4" }}>
            {[["🔒","Secure","SSL Encrypted"],
              ["📦","Tracked","DTDC Courier"],
              // ["↩️","Returns","30-day policy"],
            ].map(([icon,t,s]) => (
              <div key={t} style={{ flex:1,textAlign:"center",
                borderRight:t!=="Returns"?"1px solid #f2ddc4":"none",padding:"0 8px" }}>
                <div style={{ fontSize:18,marginBottom:3 }}>{icon}</div>
                <div style={{ fontSize:12,fontWeight:600,color:"#5c2e1f" }}>{t}</div>
                <div style={{ fontSize:10,color:"#b08060",marginTop:2 }}>{s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════
//  Stock helper
// ════════════════════════════════════════════════════════
function getStockForSize(item, size) {
  if (!size) return 0;
  if (item.sizeStock) {
    let map = item.sizeStock;
    if (map instanceof Map) map = Object.fromEntries(map);
    if (map && typeof map === "object" && !Array.isArray(map)) {
      if (map[size] !== undefined) return Number(map[size]) || 0;
    }
  }
  if (item.sizes && item.sizes.length > 0) {
    const sizes = item.sizes;
    const stock = Number(item.stock) || 0;
    const idx = sizes.indexOf(size);
    if (idx !== -1) {
      const base = Math.floor(stock / sizes.length);
      const extra = stock % sizes.length;
      return base + (idx < extra ? 1 : 0);
    }
  }
  return Number(item.stock) || 0;
}

// ════════════════════════════════════════════════════════
//  CART COMPONENT
// ════════════════════════════════════════════════════════
export default function Cart({ cartItems, setCartItems }) {
  const navigate        = useNavigate();
  const [showLogin,     setShowLogin]     = useState(false);
  const [stockWarnings, setStockWarnings] = useState([]);
  const [stockChecking, setStockChecking] = useState(true);
  const [freshStockMap, setFreshStockMap] = useState({});

  const isLoggedIn = () => {
    const role  = localStorage.getItem("userRole");
    const email = localStorage.getItem("userEmail");
    return (role === "user" && !!email) || role === "guest";
  };

  const checkRealStock = useCallback(async () => {
    if (!cartItems || cartItems.length === 0) { setStockChecking(false); return; }
    setStockChecking(true);
    const warnings = [];
    const newStockMap = {};
    try {
      const uniqueIds = [...new Set(cartItems.map(i => i._id || i.id))];
      const freshProducts = await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const res  = await fetch(`${API}/api/products/${id}`);
            const data = await res.json();
            return { id, product: data };
          } catch { return { id, product: null }; }
        })
      );
      freshProducts.forEach(({ id, product }) => {
        if (!product) return;
        const sizes = product.sizes || [];
        const totalStock = Number(product.stock) || 0;
        const sizeStock = product.sizeStock || {};
        const hasSizeStockObj = typeof sizeStock === "object" && !Array.isArray(sizeStock) && Object.keys(sizeStock).length > 0;
        if (sizes.length > 0) {
          sizes.forEach((size, idx) => {
            if (hasSizeStockObj && sizeStock[size] !== undefined) {
              newStockMap[`${id}_${size}`] = Number(sizeStock[size]) || 0;
            } else {
              const base = Math.floor(totalStock / sizes.length);
              const extra = totalStock % sizes.length;
              newStockMap[`${id}_${size}`] = base + (idx < extra ? 1 : 0);
            }
          });
        } else {
          newStockMap[`${id}_`] = totalStock;
        }
      });
      setFreshStockMap(newStockMap);
      let needsUpdate = false;
      const adjusted = cartItems.map(item => {
        const id   = item._id || item.id;
        const size = item.selectedSize || "";
        const key  = `${id}_${size}`;
        const freshStock = newStockMap[key] ?? newStockMap[`${id}_`] ?? 999;
        if (freshStock === 0) {
          warnings.push({ name:item.name, size, issue: size?`size ${size} is now sold out`:"is now sold out", removed:true });
          needsUpdate = true;
          return null;
        }
        if ((item.quantity || 1) > freshStock) {
          warnings.push({ name:item.name, size, issue:`quantity reduced to ${freshStock} (only ${freshStock} left${size?` in size ${size}`:""})`, removed:false });
          needsUpdate = true;
          return { ...item, quantity: freshStock };
        }
        return item;
      }).filter(Boolean);
      if (needsUpdate) {
        setCartItems(adjusted);
        setStockWarnings(warnings);
        setTimeout(() => setStockWarnings([]), 10000);
      }
    } catch (err) {
      console.error("Stock check failed:", err.message);
    } finally {
      setStockChecking(false);
    }
  }, [cartItems, setCartItems]);

  // Merge duplicate cart entries
  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      let changed = false;
      const seen = {};
      const merged = [];
      cartItems.forEach(item => {
        const key = `${item._id||item.id}_${item.selectedSize||""}_${item.color||""}`;
        if (seen[key]) { seen[key].quantity = (seen[key].quantity||1)+(item.quantity||1); changed=true; }
        else { seen[key]={...item}; merged.push(seen[key]); }
      });
      if (changed) setCartItems(merged);
    }
  }, [cartItems, setCartItems]);

  useEffect(() => {
    checkRealStock();
    const interval = setInterval(checkRealStock, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const handleCheckout = () => {
    if (isLoggedIn()) navigate("/checkout");
    else setShowLogin(true);
  };

  const removeItem = (itemId, selectedSize) => {
    setCartItems(cartItems.filter(item =>
      !((item._id||item.id)===itemId && item.selectedSize===selectedSize)
    ));
  };

  const updateQty = (itemId, selectedSize, delta) => {
    setCartItems(cartItems.map(item => {
      if ((item._id||item.id)===itemId && item.selectedSize===selectedSize) {
        const newQty = (item.quantity||1)+delta;
        if (newQty<1) return null;
        const freshKey   = `${itemId}_${selectedSize}`;
        const freshStock = freshStockMap[freshKey] ?? getStockForSize(item, selectedSize);
        if (newQty>freshStock) return item;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const getImageSrc = (item) => {
    const img = item.images?.[0] || item.image || "";
    if (!img)                       return "/images/s1.png";
    if (img.startsWith("http"))     return img;
    if (img.startsWith("uploads/")) return `${API}/${img}`;
    return `${API}/uploads/${img}`;
  };

  // ✅ Delivery charges are NOT shown in the cart any more. They are worked out on
  // the Checkout page, from the state of the delivery address the customer enters
  // there (see Checkout.js), so the cart total is just the items.
  const subtotal = cartItems.reduce((sum,item) => sum+(Number(item.price)*(item.quantity||1)),0);
  const total    = subtotal;

  // ── Empty cart ─────────────────────────────────────────
  if (cartItems.length===0) return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",
      justifyContent:"center",minHeight:"60vh",fontFamily:"'Segoe UI',sans-serif",
      background:"#fdf6ec",padding:40,textAlign:"center" }}>
      <div style={{ background:"#fff",border:"1px solid #f2ddc4",borderRadius:20,
        padding:"48px 56px",boxShadow:"0 4px 20px rgba(92,46,31,0.07)" }}>
        <div style={{ fontSize:64,marginBottom:16 }}>🛒</div>
        <h2 style={{ fontFamily:"Georgia,serif",color:"#4a2419",fontSize:24,marginBottom:8,fontWeight:700 }}>
          Your cart is empty
        </h2>
        <p style={{ color:"#8a4a30",fontSize:14,marginBottom:28 }}>
          Looks like you haven't added any sarees yet.
        </p>
        <button onClick={() => navigate("/home")}
          style={{ background:"#5c2e1f",color:"#fff",border:"none",
            padding:"13px 36px",fontSize:13,fontWeight:700,
            letterSpacing:"1px",cursor:"pointer",borderRadius:10,
            boxShadow:"0 4px 14px rgba(92,46,31,0.25)" }}>
          CONTINUE SHOPPING
        </button>
      </div>
    </div>
  );

  return (
    <>
      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSuccess={() => { setShowLogin(false); navigate("/checkout"); }}
        />
      )}

      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }

        /* ─── Page layout ─── */
        .cart-page-wrap { padding:32px 40px; background:#fdf6ec; min-height:100vh; font-family:'Segoe UI',sans-serif; }
        .cart-layout    { display:grid; grid-template-columns:1fr 360px; gap:24px; align-items:start; }

        /* ─── Item card row ─── */
        /*
          FIX: The item card was using CSS class "cart-item-row" for both
          the grid layout AND the card itself. On mobile the grid template
          left the remove (✕) button and the price div in different grid
          cells than the buttons, but a stacking context from a parent
          with z-index:0 (the card's border-radius + box-shadow creates
          one automatically in some browsers) was covering them.
          
          Solution: Use position:relative on the card, give interactive
          elements explicit z-index:2, and remove the conflicting
          grid-area from the remove button so it sits inside the price
          column naturally instead of being absolutely positioned.
        */
        .cart-item-card {
          position: relative;
          padding: 18px;
          background: #fff;
          margin-bottom: 12px;
          border: 1px solid #f2ddc4;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(92,46,31,0.05);
          transition: box-shadow .2s ease;

          /* Grid for desktop: image | info | qty | price+remove */
          display: grid;
          grid-template-columns: 96px 1fr 160px 130px;
          gap: 18px;
          align-items: center;
        }
        .cart-item-card:hover { box-shadow: 0 6px 20px rgba(92,46,31,0.1); }

        /* ─── Interactive elements — always on top ─── */
        .cart-qty-btn,
        .cart-remove-btn { position:relative; z-index:10; }

        .cart-qty-btn:hover:not(:disabled) { background:#f5ede0 !important; }
        .cart-remove-btn:hover { background:#fee2e2 !important; }
        .cart-checkout-btn:hover:not(:disabled) { background:#6f3610 !important; box-shadow:0 6px 18px rgba(92,46,31,0.35); }
        .cart-continue-btn:hover { background:#fdf6ec !important; border-color:#5c2e1f !important; }

        /* ─── Price + remove column ─── */
        .cart-price-col {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 8px;
        }

        /* ─── Qty stepper ─── */
        .cart-qty-wrap {
          display: flex;
          align-items: center;
          border: 1px solid #ecd9bd;
          border-radius: 20px;
          overflow: visible;   /* was hidden — clipped button hit areas */
          background: #fdf6ec;
          width: fit-content;
        }

        /* ─── Mobile ≤860px: stack summary below items ─── */
        @media (max-width:860px) {
          .cart-page-wrap { padding:20px 16px; }
          .cart-layout    { grid-template-columns:1fr; }
        }

        /* ─── Mobile ≤540px: 2-col card (image | info+qty+price) ─── */
        @media (max-width:540px) {
          .cart-item-card {
            grid-template-columns: 76px 1fr;
            grid-template-rows: auto auto auto;
            grid-template-areas:
              "img info"
              "img qty"
              "img price";
            gap: 6px 12px;
          }
          .cart-item-img-wrap  { grid-area: img;   align-self:start; }
          .cart-item-info-wrap { grid-area: info;  }
          .cart-item-qty-wrap  { grid-area: qty;   }
          .cart-item-price-col { grid-area: price; }

          /* price col goes back to row on mobile so remove sits beside price */
          .cart-price-col {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          }
        }
      `}</style>

      <div className="cart-page-wrap">

        <div style={{ marginBottom:28,display:"flex",alignItems:"baseline",gap:12 }}>
          <h1 style={{ fontFamily:"Georgia,serif",fontSize:26,color:"#4a2419",margin:0,fontWeight:700 }}>
            Shopping Cart
          </h1>
          <span style={{ background:"#f2ddc4",color:"#5c2e1f",fontSize:12,fontWeight:700,
            padding:"3px 12px",borderRadius:20 }}>
            {cartItems.length} {cartItems.length===1?"item":"items"}
          </span>
        </div>

        {stockWarnings.length>0 && (
          <div style={{ background:"#fff8e1",border:"1px solid #ffc107",
            borderRadius:12,padding:"14px 18px",marginBottom:20 }}>
            <div style={{ fontWeight:700,color:"#856404",fontSize:13,marginBottom:6 }}>
              ⚠️ Your cart was updated based on current stock:
            </div>
            {stockWarnings.map((w,i) => (
              <div key={i} style={{ fontSize:12,color:"#856404",marginBottom:3 }}>
                • <strong>{w.name}{w.size?` (Size: ${w.size})`:""}</strong>: {w.issue}
              </div>
            ))}
          </div>
        )}

        {stockChecking && (
          <div style={{ fontSize:12,color:"#8a4a30",marginBottom:12,
            display:"flex",alignItems:"center",gap:6 }}>
            <div style={{ width:10,height:10,borderRadius:"50%",
              border:"2px solid #c9a24a",borderTopColor:"transparent",
              animation:"spin .7s linear infinite" }}/>
            Verifying live stock availability…
          </div>
        )}

        <div className="cart-layout">

          {/* ── LEFT: ITEMS ── */}
          <div>
            {cartItems.map((item, index) => {
              const itemId     = item._id||item.id;
              const size       = item.selectedSize||"";
              const qty        = item.quantity||1;
              const freshKey   = `${itemId}_${size}`;
              const sizeStock  = freshStockMap[freshKey] ?? getStockForSize(item,size);
              const atMax      = qty>=sizeStock;
              const atMin      = qty<=1;
              const isLowStock = sizeStock>0 && sizeStock<=3;
              const itemTotal  = Number(item.price)*qty;

              return (
                <div key={`${itemId}_${size}_${index}`} className="cart-item-card">

                  {/* Image */}
                  <div className="cart-item-img-wrap">
                    <Link to={`/product/${itemId}`}>
                      <img src={getImageSrc(item)} alt={item.name}
                        onError={e=>{e.target.src="/images/s1.png";}}
                        style={{ width:"100%",aspectRatio:"4/5",objectFit:"cover",
                          display:"block",cursor:"pointer",borderRadius:10,
                          border:"1px solid #f2ddc4" }}/>
                    </Link>
                  </div>

                  {/* Info */}
                  <div className="cart-item-info-wrap">
                    <Link to={`/product/${itemId}`} style={{ textDecoration:"none" }}>
                      <div style={{ fontWeight:700,color:"#4a2419",fontSize:15,
                        marginBottom:5,fontFamily:"Georgia,serif" }}>
                        {item.name}
                      </div>
                    </Link>
                    {item.fabric && (
                      <div style={{ fontSize:11,color:"#8a4a30",textTransform:"uppercase",
                        letterSpacing:"0.5px",marginBottom:6 }}>
                        {item.fabric}{item.color?` · ${item.color}`:""}
                      </div>
                    )}
                    {size && (
                      <div style={{ display:"inline-block",fontSize:11,
                        background:"#fdf6ec",border:"1px solid #ecd9bd",
                        padding:"3px 10px",color:"#5c2e1f",fontWeight:700,
                        marginBottom:6,borderRadius:20 }}>
                        Size: {size}
                      </div>
                    )}
                    {isLowStock && (
                      <div style={{ fontSize:11,color:"#e65100",fontWeight:700,marginTop:4 }}>
                        ⚡ Only {sizeStock} left{size?` in size ${size}`:""}
                      </div>
                    )}
                  </div>

                  {/* Qty stepper */}
                  <div className="cart-item-qty-wrap" style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:5 }}>
                    {/*
                      FIX: overflow:hidden on the stepper container was
                      clipping the visible & clickable area of the + and −
                      buttons at their edges, making them appear to work
                      but actually swallowing click events near the border.
                      Replaced with overflow:visible + border-radius on
                      individual buttons via .cart-qty-wrap class above.
                    */}
                    <div className="cart-qty-wrap">
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQty(itemId,size,-1)}
                        disabled={atMin}
                        style={{
                          width:36, height:36, border:"none",
                          background:"transparent",
                          cursor:atMin?"not-allowed":"pointer",
                          fontSize:18, color:"#5c2e1f", fontWeight:700,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          opacity:atMin?0.35:1, transition:"background .15s",
                          borderRadius:"20px 0 0 20px",
                          /* Ensure the full button area is clickable */
                          WebkitTapHighlightColor:"transparent",
                          touchAction:"manipulation",
                          userSelect:"none",
                        }}>−</button>
                      <div style={{ minWidth:36,textAlign:"center",fontSize:13,
                        fontWeight:700,color:"#4a2419",padding:"0 4px" }}>
                        {qty}
                      </div>
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQty(itemId,size,1)}
                        disabled={atMax}
                        style={{
                          width:36, height:36, border:"none",
                          background:"transparent",
                          cursor:atMax?"not-allowed":"pointer",
                          fontSize:18, color:"#5c2e1f", fontWeight:700,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          opacity:atMax?0.35:1, transition:"background .15s",
                          borderRadius:"0 20px 20px 0",
                          WebkitTapHighlightColor:"transparent",
                          touchAction:"manipulation",
                          userSelect:"none",
                        }}>+</button>
                    </div>
                    {atMax && (
                      <div style={{ fontSize:10,color:"#c62828",fontWeight:600,textAlign:"center" }}>
                        Max {sizeStock}{size?` (${size})`:""}
                      </div>
                    )}
                    {!atMax && size && sizeStock<=10 && (
                      <div style={{ fontSize:10,color:"#8a4a30",textAlign:"center" }}>
                        {sizeStock} left
                      </div>
                    )}
                  </div>

                  {/* Price + Remove */}
                  <div className="cart-item-price-col cart-price-col">
                    <div>
                      <div style={{ fontWeight:700,color:"#5c2e1f",fontSize:16,
                        fontFamily:"Georgia,serif",textAlign:"right" }}>
                        ₹{itemTotal.toLocaleString()}
                      </div>
                      {item.originalPrice>item.price && (
                        <div style={{ fontSize:11,color:"#b0a090",
                          textDecoration:"line-through",textAlign:"right" }}>
                          ₹{(Number(item.originalPrice)*qty).toLocaleString()}
                        </div>
                      )}
                    </div>
                    {/*
                      FIX: Remove button was position:absolute which put it
                      into the stacking context of the card. On mobile some
                      browsers render the card's border-radius / box-shadow
                      as a new stacking context, pushing the absolutely-
                      positioned remove button behind the card background.
                      Now it's just a flex child — no absolute positioning,
                      no z-index fights, always clickable.
                    */}
                    <button
                      className="cart-remove-btn"
                      onClick={() => removeItem(itemId,size)}
                      title="Remove item"
                      style={{
                        width:32, height:32,
                        border:"1.5px solid #fecaca",
                        background:"#fff5f5",
                        cursor:"pointer",
                        color:"#dc2626",
                        fontSize:13, fontWeight:700,
                        borderRadius:"50%",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        transition:"all .15s", flexShrink:0,
                        WebkitTapHighlightColor:"transparent",
                        touchAction:"manipulation",
                      }}>✕</button>
                  </div>

                </div>
              );
            })}

            <button className="cart-continue-btn" onClick={() => navigate("/home")}
              style={{ marginTop:12,background:"#fff",color:"#5c2e1f",
                border:"1.5px solid #ecd9bd",padding:"11px 22px",
                fontSize:12,fontWeight:700,cursor:"pointer",
                letterSpacing:"0.8px",borderRadius:8,transition:"all .15s" }}>
              ← Continue Shopping
            </button>
          </div>

          {/* ── RIGHT: ORDER SUMMARY ── */}
          <div style={{ background:"#fff",border:"1px solid #f2ddc4",
            padding:"26px",borderRadius:14,position:"sticky",top:20,
            boxShadow:"0 4px 20px rgba(92,46,31,0.07)" }}>

            <h3 style={{ fontFamily:"Georgia,serif",fontSize:17,color:"#4a2419",
              margin:"0 0 20px",paddingBottom:14,borderBottom:"1px solid #f2ddc4",fontWeight:700 }}>
              Order Summary
            </h3>

            {/* Item breakdown */}
            <div style={{ marginBottom:14,display:"flex",flexDirection:"column",
              gap:9,maxHeight:220,overflowY:"auto" }}>
              {cartItems.map((item,i) => {
                const sz = item.selectedSize||"";
                return (
                  <div key={i} style={{ display:"flex",justifyContent:"space-between",fontSize:12,color:"#7a5a30" }}>
                    <span style={{ flex:1,overflow:"hidden",textOverflow:"ellipsis",
                      whiteSpace:"nowrap",paddingRight:8 }}>
                      {item.name}
                      {sz && <span style={{ color:"#c8a97a" }}> · {sz}</span>}
                      <span style={{ color:"#8a4a30" }}> ×{item.quantity||1}</span>
                    </span>
                    <span style={{ fontWeight:700,color:"#4a2419",flexShrink:0 }}>
                      ₹{(Number(item.price)*(item.quantity||1)).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div style={{ display:"flex",flexDirection:"column",gap:10,
              marginBottom:16,paddingTop:14,borderTop:"1px dashed #ecd9bd" }}>
              <div style={{ display:"flex",justifyContent:"space-between",fontSize:13,color:"#5c2e1f" }}>
                <span>Subtotal ({cartItems.length} {cartItems.length===1?"item":"items"})</span>
                <span style={{ fontWeight:700 }}>₹{subtotal.toLocaleString()}</span>
              </div>
              {/* ✅ REMOVED: shipping breakdown box — replaced with a simple
                  one-line note. No AP/Telangana rate-zone text, no expanded
                  breakdown that confused users on mobile. */}
              <div style={{ fontSize:11,color:"#8a4a30" }}>
                🚚 Up to 4 Blouses – Standard Delivery(Delivery time depends on your location)
              </div>
              {/* <div style={{ fontSize:11,color:"#8a4a30" }}>
                Delivery charges are added at checkout, based on your state.
              </div> */}
            </div>

            {/* Grand total */}
            <div style={{ display:"flex",justifyContent:"space-between",
              padding:"16px 0",borderTop:"2px solid #f2ddc4",marginBottom:20 }}>
              <span style={{ fontFamily:"Georgia,serif",fontSize:17,color:"#4a2419",fontWeight:700 }}>
                Total
              </span>
              <span style={{ fontFamily:"Georgia,serif",fontSize:22,color:"#5c2e1f",fontWeight:800 }}>
                ₹{total.toLocaleString()}
              </span>
            </div>

            <button className="cart-checkout-btn" onClick={handleCheckout} disabled={stockChecking}
              style={{ width:"100%",padding:"15px",
                background:stockChecking?"#ccc":"#5c2e1f",
                color:"#fff",border:"none",fontSize:13,fontWeight:700,
                letterSpacing:"1px",cursor:stockChecking?"not-allowed":"pointer",
                marginBottom:12,borderRadius:10,transition:"all .2s",
                boxShadow:stockChecking?"none":"0 4px 14px rgba(92,46,31,0.25)" }}>
              {stockChecking?"Verifying stock…":"PROCEED TO CHECKOUT →"}
            </button>

            <div style={{ display:"flex",justifyContent:"center",gap:18,marginTop:14,
              paddingTop:14,borderTop:"1px solid #f5ede0" }}>
              {["🔒 Secure","✅ Authentic"].map(b => (
                <div key={b} style={{ fontSize:10,color:"#8a4a30",textAlign:"center",fontWeight:600 }}>
                  {b}
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}